<template>

  <div id="modal-point-searcher">
    <div class="modal-point-search-form">
      <button 
        class="uk-form-icon uk-form-icon-flip searcher"
        @click="onOpenModal()"
        uk-icon="icon: search"
      />
    
      <input
        class="keyword-input uk-input"
        type="text"
        :placeholder="messages.points_keyword_placeholder"
        @keydown.enter="onOpenModalByEnter" 
        enterkeyhint="search"
        v-model="query"
      >
    </div>

    <div uk-modal id="store-search-modal">
    
      <div class="store-search-body uk-modal-dialog uk-padding-remove">
        <button class="uk-modal-close-default" type="button" uk-close></button>
        
        <div class="uk-modal-header">
          <h2 class="uk-modal-title">{{ messages.points_keyword_placeholder }}</h2>
        </div>
    
        <div class="store-search-actions uk-flex uk-padding-small">
          <div class="modal-point-search-form">
            <button 
              class="uk-form-icon uk-form-icon-flip searcher"
              @click="onPointsSearch()"            
              uk-icon="icon: search"
            />
          
            <input
              class="keyword-input uk-input"
              type="text"
              :placeholder="messages.points_keyword_placeholder"
              @keydown.enter="onPointsSearchByEnter"
              enterkeyhint="search"
              v-model="query"
            >
          </div>
        </div>

        <div class="point-search-results-summary">
          <p v-if="errorMessage" class="error-message" v-html="errorMessage"></p>          
          <p v-else v-html="resultMessage"></p>
        </div>
        
        <div class="point-search-results uk-padding-small uk-modal-body" uk-overflow-auto>
          
          <div class="loading-container" v-if="isLoading">
            <div uk-spinner="ratio: 1.5"></div>
          </div>
    
          <ul class="uk-list uk-list-divider">
            <li v-for="point in foundPoints" :key="point.key">
              <button class="uk-modal-close" :data-key="point.key" @click="onPointSelected(point)" type="button">
                <div class="point-contents">
                  <p class="name">{{ point.name }}</p>
                  <p class="address">{{ point.address }}</p>
                </div>
                <span class="left-icon" uk-icon="icon: chevron-right"></span>
              </button>          
            </li>
            <li v-if="foundPoints.length >= 100"><p class="over-result-message">{{ messages.common_points_found_over_annotation.replace('{points_count}', foundPoints.length) }}</p></li>
          </ul>
        </div>
      </div>
    </div>
  </div>
  </template>
  
<script setup>
  /**
   * 地図ページでのキーワード検索（モーダル表示）のコンポーネント
   * 
   * design.jsonのmap.filterでキーワード検索を有効、且つ modal.enabled をtrueに設定すると有効となる
   * modal指定の無いキーワード検索と異なり、検索結果をモーダル内で一覧表示し、任意のポイントへ地図移動 + 選択する
   * 検索結果には絞り込みの状態が反映される
   * 
   * 一度ポイントを全件取得するので、ポイントが数万件規模 or 追加フィールドが極端に多い場合の利用は推奨しない
   * 
   * 親コンポーネントからも onOpenModal(キーワード)でモーダルを開き、検索を実行可能
   */

  import { ref, computed, watch, onMounted } from 'vue'
  import { useStore } from 'vuex'
  import uikit from "uikit"  

  import { PointMarker } from "@/points/PointMarker.ts"
  import { LocationManager } from "@/services/Location"
  import { Filters } from '@/services/Filters.ts'
  import { sortByDistance } from '@/utils/common.js'
  
  import MapService from '@/services/MapService.ts'
  import PointService from '@/services/PointService.ts'
  

  const props = defineProps({  
    /**
     * キーワード検索 filter オブジェクト（design.jsonのmap.filter.keywords）
     * 以下のようなものを想定し、modal.enabled が true の場合このコンポーネントが有効になる
     * 
     * "keywords": {
     *   "enabled": true, // キーワード検索の有効化
     *   "targets": ["name", "address"], // 検索するフィールド
     *   "modal": {
     *     "enabled": true,　// モーダル表示有効化
     *     "open_initial": true, // 地図表示時、URLに検索ワード指定がある場合モーダルをひらく
     *     "open_initial_query_key": "sw" // URLに含める検索ワードのキー
     *     "sortByDistance": true, // true にすると、距離順でソート
     *     "applyFilters": true, // 一応用意しているが、 true で運用すること（falseにする場合動作調整が必要）
     *   }          
     * }
     */
    filter: Object
  })

  const store = useStore()

  /**
   * 検索結果に表示する上限
   * 上限を超えると、超えた分を省略 + メッセージ表示
   */
   const SEARCH_LIMIT = 100  

  /**
   * ローディング表示状態
   */
  const isLoading = ref(false)
  
  /**
   * 検索実行済みの場合 true
   * 検索結果メッセージ表示用
   */
  const isSearchExecuted = ref(false)

  /**
   * エラーメッセージ
   * テキストを入れるとメッセージを表示する
   */
  const errorMessage = ref("")

  /**
   * 管理画面から設定した表記
   */
  const messages = ref(storelocator.messages)

  /**
   * 検索フォームと紐づいている検索クエリ
   */  
  const query = ref("")  

  /**
   * APIから返却されたポイントを格納
   * 現状ではサーバーから全件取得しており、キャッシュとして利用するのでリアクティブにしない
   */  
  const responded_points = []

  /**
   * 検索結果に表示するポイントを格納
   */  
  const foundPoints = ref([])

  /**
   * 絞り込みで非表示となっている店舗を選択する場合など、マーカーが非表示の場合に暫定的に表示するマーカーを格納
   */
  let _tmpMarker = null

  
  // 検索結果メッセージ
  const resultMessage = computed(() => {
    if (!isSearchExecuted.value) {
      // メッセージなし（初期状態）
      return ""
    } else if (foundPoints.value.length == 0) {
      // 検索結果に該当なし
      return messages.value.map_not_found_filter
    } else if (foundPoints.value.length < 100) {
      // 検索結果表示
      return messages.value.common_points_found.replace('{points_count}', foundPoints.value.length)
    } else if (foundPoints.value.length >= 100) {
      // 検索結果表示表示 + 100件オーバー
      return messages.value.common_points_found_over.replace('{points_count}', "100")
    }
  
    return ""
  })

  /**
   * エンターでモーダルを開く
   * 検索クエリが入力されていた場合は一緒に検索を実行する
   */
  function onOpenModalByEnter(e) {
    if (e.isComposing || e.keyCode === 229) return
    if (e.key == "Enter") {
      onOpenModal()
    }
  }

  /** 
   * モーダルを開く
   * 検索クエリが入力されていた場合は一緒に検索を実行する 
   * 引数に query の指定がなければ、入力フォームに入力されたクエリを使う
   * 
   * @param query {String} 検索クエリ(キーワード)引数で指定するのは、親コンポーネントからクエリ指定で直接検索をかけるケースを想定
   */
  function onOpenModal(query = null) {
    uikit.modal("#store-search-modal").show()
    onPointsSearch(query)  
  }

  /**
   * モーダルを閉じる際の処理
   */
   const onClose = () => {
    query.value = ""    
    isSearchExecuted.value = false
    errorMessage.value = ""
    foundPoints.value.splice(0)
  }
  
  /**
   * エンターで検索実行
   * @param e {Object} keydownイベント
   */
  async function onPointsSearchByEnter (e) {
    if (e.isComposing || e.keyCode === 229) return
    if (e.key == "Enter") {
      await onPointsSearch()
    }
  }
  
  /**
   * 検索実行
   * 引数に query がある場合、検索フォームに入力された値を引数のクエリに置き換える
   * モーダルを開きながら検索実行する場合は、onOpenModal()の方を使う
   * 
   * TODO 要 検索速度対策
   * 現在全件取得して画面側で絞り込んでいるので、ポイント数が多いと時間がかかる
   * 特に1万件を超えるようなケースは注意
   * 
   * @param overrideQuery {String} 指定すると、検索フォームのクエリを置き換えて実行する
   */
  async function onPointsSearch(overrideQuery = null) {    
    errorMessage.value = ""

    // 引数に検索ワード指定がある場合はそちらを優先
    if (overrideQuery) {
      query.value = overrideQuery
    }

    // 無いと思いつつ、結果が返る前に検索ワードが変わる可能性を考慮してプリミティブ値にしておく
    const _query = query.value
  
    if (!_query || _query == "") {
      return 
    }

    if (responded_points.length == 0) {
      let response = null
      try {
        // 検索実行
        isLoading.value = true            
        response = await PointService.fetchPoints()
      } catch(e) {
        // サーバー側エラー表示
        errorMessage.value = messages.value.common_failed_to_load_points
        isLoading.value = false      
        return
      }
      isLoading.value = false
      const searchResult = response.data
      
      // 読み込み済みポイントを更新
      await PointService.onLoadPoints(
        searchResult.items.map(p => PointService.parsePoint(p))
      )

      // コンポーネント内のキャッシュ更新
      responded_points.push(...searchResult.items)
    }
    
    let points = [...responded_points]
    

    // 検索ワードで絞り込み
    points = filterByQuery(points, _query)

    // 有効なら検索結果に絞り込み条件を反映
    if (props.filter.modal?.applyFilters) {
      points = PointService._getFilteredPoints(points)
    }

    // 有効なら距離ソート（現在地 or 中心）
    if (props.filter.modal?.sortByDistance) {
      let basePosition = MapService.map.getCenter()    
      try {
        basePosition = await LocationManager.getCurrentPosition()
      } catch(e) {
        console.warn("Current location failed. sort by map center.")
      }
      
      points = sortByDistance(basePosition, points)
    }

   
    // 件数がSEARCH_LIMIT以上だった場合はSEARCH_LIMITまで表示
    if (points.length >= SEARCH_LIMIT) {
      points.splice(SEARCH_LIMIT)
    }
    
    // 検索結果反映
    isSearchExecuted.value = true
    foundPoints.value.splice(0)
    foundPoints.value.push(...points)

    // 分析用イベント送信（検索ワード）
    storelocator.analytics.client?.send(
      'SearchByKeyword',
      {
        ModalSearchQuery: _query,
        ModalSearchHitCount: points.length,
      }
    )
  }

  /**
   * ポイントを選択した際の処理
   * 該当のポイントまで地図を移動し、マーカー選択状態にする
   * 
   * 絞り込みで非表示の場合は暫定的にマーカーを作って表示
   * 
   * @param point {Point} 選択するポイント
   */
  async function onPointSelected(point) {
    onClose()

    // 座標計算
    point.position = new google.maps.LatLng(
      point.latitude,
      point.longitude
    )

    // 選択ポイントが非表示だったらマーカーを暫定的に表示する
    // 絞り込み等でマーカーが非表示の場合、強制的にマーカーを表示させるために用いる
    // 現在のユースケースでは不要なのでコメントアウト

    // マーカー絞り込みの状態
    // const _markerState = Filters.findByIds(
    //   Filters.TYPE_MARKERS,
    //   Filters.selectedFilters.markers
    // )
    
    // 検索条件絞り込みの状態
    // const _searchConditionState = Filters.findByIds(
    //   Filters.TYPE_SEARCH_CONDITIONS,
    //   Filters.selectedFilters.search_conditions,
    //   true
    // )

    // if (!PointService.checkPoint(point, _markerState, _searchConditionState)) {      
    //   _tmpMarker = new PointMarker(
    //     MapService.map,
    //     new google.maps.LatLng(point.latitude, point.longitude),
    //     null,
    //     {
    //       point: point,
    //       selected: true,
    //       clickable: true,
    //       zIndex: 100000020, // 100000000 までは南マーカー優先表示に使っているのでそれ以上の値を指定
    //       isWithinCluster: true,
    //       onClicked: () => { MapService.pointManager.onInnerMarkerClicked() }
    //     }
    //   )
    // }

    // infoWindow表示（地図も移動動する）
    PointService.showPoint(point.position)

    _selectMarker(point)
  }

  /**
   * マーカーを選択状態にする
   * 地図移動後マーカー描画前だと選択できないので、バウンズ内のポイントが更新されるまで
   * 250ms感覚 × 10回まで再起的に実行
   * 
   * @param point {Point} 選択するポイント
   * @param count {Int} 試行回数
   */
  async function _selectMarker(point, count = 0) {
    if (count > 10) {
      console.warn("selectMarker retry count over. marker select failed.")
      return
    }

    // バウンズ内にポイントがあるか確認し、なければ遅延してリトライ
    if (!store.state.points.pointsInBounds.some(p => p.id === point.id)) {
      await new Promise(resolve => setTimeout(resolve, 250))
      _selectMarker(point, count + 1)
      return
    }
      
    MapService.pointManager.clearPoint()  
    MapService.pointManager.selectPoint(point.id)
    store.dispatch("selectPoint", point)
  
    // 分析用のイベントを送信（店舗選択）
    storelocator.analytics.client?.send('SelectPoint', {
      PointName: point.name,
      PointKey: point.key,
      PointAddress: point.address,
      ZoomLevel: MapService.map.getZoom()
    })
  }

  /**
   * 検索ワードでの検索結果の絞り込み
   * 
   * MEMO
   * 現在画面表示で絞り込んでいるが、サーバ側で絞り込むよう変更する場合は不要
   * 
   * @param points 
   * @param query 
   */
  function filterByQuery (points, query) {
    return points.filter(p => {      
      const searchWords = props.filter.targets.reduce((result, field) => {        
        let word = ""

        if (p[field] != null) {
          word = p[field]
        } else if (p.extra_fields[field] != null) {
          word = p.extra_fields[field]
        }
        
        return result + word

      }, "")
      return searchWords.includes(query)
    })
  }

  /**
   * 暫定的に作成したマーカーを消す処理
   * ポイント選択状態を監視して、更新された場合は消す
   */
  watch(() => store.getters.selectedPoint, () => {    
    if (_tmpMarker) {
      _tmpMarker.remove()
      _tmpMarker = null
    }
  })

  // 親コンポーネントから呼べるように公開しておく
  defineExpose({
    onOpenModal
  })

  onMounted(() => {
    // 閉じる時にリセット
    uikit.util.on("#store-search-modal", 'beforehide',async (e) => {
      onClose()
    })

    // 初期表示時モーダルを開く機能が有効だった場合は開いて検索を実行
    if (props.filter.modal?.open_initial) {
      const queryKey = props.filter.modal?.open_initial_query_key ?? ""
      const query = storelocator?.url_params[queryKey] ?? null

      if (query) {
        onOpenModal(query)
      }
    }
  })

  </script>
  
  <style lang="scss">

    .modal-point-search-form {
      width: 100%;
      position: relative;
      transition: all .2s;
      
      .keyword-input {
        width: 100%;
        background: #eee;
        border: none;
        border-radius: 20px;
        padding-left: 16px;
        transition: all .25s;
    
        &:focus {
          background: #777;
          border-color: #777 !important;
          color: #fff;
          transition: all .25s;
    
          &::placeholder {
            color: #aaa;
          }
        }        
      }
      
      button.uk-form-icon {
        border: none;
        border-radius: 9999px;
        margin: 2px;
        transition: all .25s;        
      }

      &:focus, &:focus-within {
        button.uk-form-icon {
          background-color: #ffff;
          color: var(--theme-color);
        }
      }

      button.uk-form-icon:hover {
        background-color: var(--theme-color);
        color: #fff;
      }
    }

    #modal-point-searcher {
    }

    #store-search-modal {
      .uk-modal-header {
        padding-left: 15px;

        .uk-modal-title {
          font-size: 1rem;
        }
      }

      .loading-container {
        text-align: center;
      }

      // 検索結果メッセージ
      .point-search-results-summary {
        padding: 15px;
        font-size: .875rem;
        p {
          margin: 0;
          &.error-message {
            color: #c00
          }
        }
      }

      // 検索結果が100件以上の時のメッセージ
      .over-result-message {
        font-size: .875rem;
        margin: 0;
      }

      .point-search-results {
        border-top: solid 1px #ddd;
        button {
          width: 100%;
          border: none;
          background: none;
          display: flex;
          justify-content: space-between;
          align-items: center;
          text-align: left;
          padding: 0;
          cursor: pointer;
          
          .point-contents {
            flex-grow: 1;
            .name{
              font-size: 1rem;
              margin-bottom: .25em;
            }
      
            .address{ 
              font-size: .75rem;              
              color: #888;
              margin: 0;
            }
          }

          .left-icon {
            opacity: 0.3;
            color: #000;
            flex-shrink: 1;
            transform: translateX(0);
            transition: all .25s;
          }

          &:hover {
            .left-icon {
              opacity: 1;
              color: var(--theme-color);
              transform: translateX(4px) scale(1.25);
            }
            
          }
        }
      }
    }
  </style>